import React from 'react';
import { Package, Calendar, Truck, MapPin, User, Clock, Mail, Phone } from 'lucide-react';
import { OrderTimelineDto } from '../../../../../types/orderTimeline';

interface TimelineHeaderProps {
  data: OrderTimelineDto;
}

const formatDate = (dateStr?: string) => {
  if (!dateStr) return '–';
  const d = new Date(dateStr);
  return d.toLocaleDateString('nb-NO', { day: '2-digit', month: '2-digit', year: 'numeric' });
};

const formatDateTime = (dateStr?: string) => {
  if (!dateStr) return '–';
  const d = new Date(dateStr);
  return d.toLocaleDateString('nb-NO', { day: '2-digit', month: '2-digit', year: 'numeric' }) + ' ' +
    d.toLocaleTimeString('nb-NO', { hour: '2-digit', minute: '2-digit' });
};

export const TimelineHeader: React.FC<TimelineHeaderProps> = ({ data }) => {
  const statusColor = data.orderStatus >= 60 ? 'bg-green-100 text-green-800 border-green-200' :
    data.orderStatus >= 40 ? 'bg-blue-100 text-blue-800 border-blue-200' :
    data.orderStatus >= 20 ? 'bg-yellow-100 text-yellow-800 border-yellow-200' :
    'bg-gray-100 text-gray-800 border-gray-200';

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-4 mb-4">
      {/* Title row */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-3">
          <Package className="w-5 h-5 text-blue-600" />
          <h2 className="text-lg font-semibold text-gray-900">
            Ordre {data.orderNo}
          </h2>
          {data.orderNoERP && (
            <span className="text-sm text-gray-500">ERP: {data.orderNoERP}</span>
          )}
        </div>
        <span className={`px-3 py-1 text-sm font-medium rounded-full border ${statusColor}`}>
          {data.orderStatusText}
        </span>
      </div>

      {/* Info grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
        {data.customerName && (
          <div className="flex items-center gap-2">
            <User className="w-4 h-4 text-gray-400" />
            <div>
              <div className="text-gray-500 text-xs">Kunde</div>
              <div className="font-medium text-gray-800">{data.customerName}</div>
              {data.customerNo && <div className="text-xs text-gray-400">{data.customerNo}</div>}
            </div>
          </div>
        )}
        {data.orderDate && (
          <div className="flex items-center gap-2">
            <Calendar className="w-4 h-4 text-gray-400" />
            <div>
              <div className="text-gray-500 text-xs">Ordredato</div>
              <div className="font-medium text-gray-800">{formatDate(data.orderDate)}</div>
            </div>
          </div>
        )}
        {data.deliveryDate && (
          <div className="flex items-center gap-2">
            <Truck className="w-4 h-4 text-gray-400" />
            <div>
              <div className="text-gray-500 text-xs">Leveringsdato</div>
              <div className="font-medium text-gray-800">{formatDate(data.deliveryDate)}</div>
            </div>
          </div>
        )}
        {data.warehouse && (
          <div className="flex items-center gap-2">
            <MapPin className="w-4 h-4 text-gray-400" />
            <div>
              <div className="text-gray-500 text-xs">Lager</div>
              <div className="font-medium text-gray-800">{data.warehouse}</div>
            </div>
          </div>
        )}
      </div>

      {/* Delivery address */}
      {data.delCustomerName && (
        <div className="mt-3 pt-3 border-t border-gray-100 text-sm">
          <div className="text-gray-500 text-xs mb-1">Leveringsadresse</div>
          <div className="text-gray-700">
            {data.delCustomerName}
            {data.delAddress1 && <>, {data.delAddress1}</>}
            {data.delZipCode && data.delCity && <>, {data.delZipCode} {data.delCity}</>}
          </div>
        </div>
      )}

      {/* References & contact */}
      <div className="mt-3 pt-3 border-t border-gray-100 flex flex-wrap gap-4 text-xs text-gray-500">
        {data.customerOrderNo && <span>Kundeordre: <strong className="text-gray-700">{data.customerOrderNo}</strong></span>}
        {data.carrierName && <span>Transportør: <strong className="text-gray-700">{data.carrierName}</strong></span>}
        {data.yourRef && <span>Deres ref: <strong className="text-gray-700">{data.yourRef}</strong></span>}
        {data.ourRef && <span>Vår ref: <strong className="text-gray-700">{data.ourRef}</strong></span>}
        {data.orderCreated && (
          <span className="flex items-center gap-1">
            <Clock className="w-3 h-3" /> Opprettet: <strong className="text-gray-700">{formatDateTime(data.orderCreated)}</strong>
            {data.orderCreatedBy && <> av {data.orderCreatedBy}</>}
          </span>
        )}
        {data.eMailAddress && (
          <span className="flex items-center gap-1"><Mail className="w-3 h-3" /> {data.eMailAddress}</span>
        )}
        {data.telephone && (
          <span className="flex items-center gap-1"><Phone className="w-3 h-3" /> {data.telephone}</span>
        )}
      </div>
    </div>
  );
};
